// src/context/FormContext.js - Main context provider and hooks

import React, { createContext, useContext, useReducer, useCallback, useMemo, useEffect } from 'react';
import api from '../api';

// Import separated modules
import { ActionTypes, initialState, getDefaultRegionFormState } from './FormTypes';
import { formReducer, computedHelpers } from './FormReducer';
import {
  saveToLocalStorage,
  loadFromLocalStorage,
  clearLocalStorage,
  hasSavedData,
  getSavedDataInfo,
  serializeFormState // Import serialize helper
} from '../utils/localStorage';
import { base64ToBlob } from '../utils/fileUtils'; // Import the utility

// Create Context
const FormContext = createContext();

// Provider Component
export const FormProvider = ({ children }) => {
  const [state, dispatch] = useReducer(formReducer, initialState);

  // Load saved data on app startup (disabled to prevent cropping issues)
  // useEffect(() => {
  //   const loadSavedData = async () => {
  //     try {
  //       console.log('🔄 Checking for saved form data on startup...');
  //       const savedState = await loadFromLocalStorage();
  //       if (savedState) {
  //         // Merge saved state into current state
  //         Object.entries(savedState).forEach(([key, value]) => {
  //           if (value !== null && value !== undefined) {
  //             dispatch({ type: 'RESTORE_FROM_STORAGE', payload: { key, value } });
  //           }
  //         });
  //         console.log('✅ Form data restored from localStorage');
  //       }
  //     } catch (error) {
  //       console.error('❌ Failed to load saved data:', error);
  //     }
  //   };

  //   loadSavedData();
  // }, []);

  // Remove auto-save to prevent infinite re-renders

  // Auto-restore images on tab changes
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch({ type: ActionTypes.AUTO_RESTORE_IMAGES });
    }, 100);

    return () => clearTimeout(timer);
  }, [state.currentTab]);

  // Memoized actions to prevent recreation on every render
  const actions = useMemo(() => ({
    // Basic state actions
    setPatientData: (data) => {
      dispatch({ type: ActionTypes.SET_PATIENT_DATA, payload: data });
    },

    setCanalsTraced: (data) => {
      dispatch({ type: ActionTypes.SET_CANALS_TRACED, payload: data });
    },

    setRadiologicalImpressions: (data) => {
      dispatch({ type: ActionTypes.SET_RADIOLOGICAL_IMPRESSIONS, payload: data });
    },

    setOtherImpression: (data) => {
      dispatch({ type: ActionTypes.SET_OTHER_IMPRESSION, payload: data });
    },

    setSinusViewData: (data) => {
      dispatch({ type: ActionTypes.SET_SINUS_VIEW_DATA, payload: data });
    },

    setSagittalViewData: (data) => {
      dispatch({ type: ActionTypes.SET_SAGITTAL_VIEW_DATA, payload: data });
    },

    setPanoramicData: (data) => {
      dispatch({ type: ActionTypes.SET_PANORAMIC_DATA, payload: data });
    },

    setDimensionsData: (data) => {
      dispatch({ type: ActionTypes.SET_DIMENSIONS_DATA, payload: data });
    },

    setRegionData: (regionNumber, data) => {
      dispatch({
        type: ActionTypes.SET_REGION_DATA,
        payload: { regionNumber, data }
      });
    },

    updateRegionData: (regionNumber, updates) => {
      dispatch({
        type: ActionTypes.UPDATE_REGION_DATA,
        payload: { regionNumber, updates }
      });
    },

    removeRegionData: (regionNumber) => {
      dispatch({
        type: ActionTypes.REMOVE_REGION_DATA,
        payload: { regionNumber }
      });
    },

    setReportType: (reportType) => {
      dispatch({ type: ActionTypes.SET_REPORT_TYPE, payload: reportType });
    },

    setSelectedUnit: (unit) => {
      dispatch({ type: ActionTypes.SET_SELECTED_UNIT, payload: unit });
    },

    setSelectedDoctor: (doctor) => {
      dispatch({ type: ActionTypes.SET_SELECTED_DOCTOR, payload: doctor });
    },

    setSelectedFile: (file) => {
      dispatch({ type: ActionTypes.SET_SELECTED_FILE, payload: file });
    },

    setCurrentTab: (tab) => {
      dispatch({ type: ActionTypes.SET_CURRENT_TAB, payload: tab });
    },

    setSidebarOpen: (isOpen) => {
      dispatch({ type: ActionTypes.SET_SIDEBAR_OPEN, payload: isOpen });
    },

    toggleSidebar: () => {
      dispatch({ type: ActionTypes.TOGGLE_SIDEBAR });
    },

    setImpactionData: (data) => {
      dispatch({ type: ActionTypes.SET_IMPACTION_DATA, payload: data });
    },

    setLoading: (loading) => {
      dispatch({ type: ActionTypes.SET_LOADING, payload: loading });
    },

    setError: (error) => {
      dispatch({ type: ActionTypes.SET_ERROR, payload: error });
    },

    clearError: () => {
      dispatch({ type: ActionTypes.CLEAR_ERROR });
    },

    resetForm: () => {
      dispatch({ type: ActionTypes.RESET_FORM });
    },

    setIsRestoring: (isRestoring) => {
      dispatch({ type: ActionTypes.SET_IS_RESTORING, payload: isRestoring });
    },

    restoreState: (savedState) => {
      dispatch({ type: ActionTypes.RESTORE_STATE, payload: savedState });
    },

    // Persistent UI state actions
    updateDimensionsPdfState: (updates) => {
      dispatch({ type: ActionTypes.UPDATE_DIMENSIONS_PDF_STATE, payload: updates });
    },

    updateRegionDetailsPdfState: (updates) => {
      dispatch({ type: ActionTypes.UPDATE_REGION_DETAILS_PDF_STATE, payload: updates });
    },

    updatePanoramicUIState: (updates) => {
      dispatch({ type: ActionTypes.UPDATE_PANORAMIC_UI_STATE, payload: updates });
    },

    updateCanalsUIState: (updates) => {
      dispatch({ type: ActionTypes.UPDATE_CANALS_UI_STATE, payload: updates });
    },

    updateRegionFormState: (regionNumber, updates) => {
      dispatch({
        type: ActionTypes.UPDATE_REGION_FORM_STATE,
        payload: { regionNumber, updates }
      });
    },

    // Image management actions
    autoRestoreImages: () => {
      dispatch({ type: ActionTypes.AUTO_RESTORE_IMAGES });
    },

    cleanupRegionUrls: (regionNumber) => {
      dispatch({
        type: ActionTypes.CLEANUP_BLOB_URLS,
        payload: { regionNumber }
      });
    },

    // Helper to get region form state with defaults
    getRegionFormState: (regionNumber) => {
      return state.regionFormStates[regionNumber] || getDefaultRegionFormState();
    },

    // localStorage management actions
    saveProgress: async () => {
      try {
        const success = await saveToLocalStorage(state);
        if (success) {
          console.log('✅ Progress saved manually');
        }
        return success;
      } catch (error) {
        console.error('❌ Manual save failed:', error);
        return false;
      }
    },

    loadProgress: async () => {
      try {
        actions.setIsRestoring(true);
        const savedState = await loadFromLocalStorage();
        if (savedState) {
          actions.restoreState(savedState);
          console.log('✅ Progress loaded manually');
          return true;
        }
        return false;
      } catch (error) {
        console.error('❌ Manual load failed:', error);
        return false;
      } finally {
        actions.setIsRestoring(false);
      }
    },

    clearProgress: () => {
      try {
        clearLocalStorage();
        console.log('✅ Saved progress cleared');
        return true;
      } catch (error) {
        console.error('❌ Failed to clear progress:', error);
        return false;
      }
    },

    getSavedInfo: () => {
      return getSavedDataInfo();
    },

    hasSavedProgress: () => {
      return hasSavedData();
    }
  }), [state]);

  // Memoized computed values
  const computed = useMemo(() => ({
    isFormValid: () => computedHelpers.isFormValid(state),
    getConsolidatedReportData: () => computedHelpers.getConsolidatedReportData(state),
    hasUnsavedUIState: () => computedHelpers.hasUnsavedUIState(state),
    getImageStateDiagnostics: () => computedHelpers.getImageStateDiagnostics(state)
  }), [state]);

  // Memoized context value
  const contextValue = useMemo(() => ({
    state,
    actions,
    computed
  }), [state, actions, computed]);

  return (
    <FormContext.Provider value={contextValue}>
      {children}
    </FormContext.Provider>
  );
};

// Custom hook to use the context
export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};

// Form validation hook
export const useFormValidation = () => {
  const { state } = useFormContext();

  const validateForm = useCallback(() => {
    const errors = [];

    if (!state.patientData) {
      errors.push('Patient data is required');
    }

    if (!state.reportType || state.reportType === 'Report Type') {
      errors.push('Report type must be selected');
    }

    if (!state.selectedFile) {
      errors.push('DICOM file must be selected');
    }

    if (state.selectedUnit === 'UNIT Number') {
      errors.push('Unit must be selected');
    }

    if (state.selectedDoctor === 'Doctor Name') {
      errors.push('Doctor must be selected');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }, [state.patientData, state.reportType, state.selectedFile, state.selectedUnit, state.selectedDoctor]);

  return { validateForm };
};

// Enhanced form submission hook with better error handling
export const useFormSubmission = () => {
  const { state, actions } = useFormContext();

  const prepareFormData = useCallback(() => {
    const formData = new FormData();

    console.log('🔧 Preparing form data for submission...');

    // Add main data as JSON
    const mainData = {
      patientData: state.patientData,
      reportType: state.reportType,
      selectedUnit: state.selectedUnit,
      canalsTraced: state.canalsTraced,
      radiologicalImpressions: state.radiologicalImpressions,
      otherImpression: state.otherImpression,
      panoramicViewData: {
        comments: state.panoramicViewData?.comments || ''
      },
      sinusViewData: {
        selectedImpressions: state.sinusViewData?.selectedImpressions || [],
        otherImpression: state.sinusViewData?.otherImpression || ''
      },
      sagittalViewData: state.sagittalViewData || { image: null, mainComment: '', mandibleComment: '' },
      regionDetailsData: {},
      dimensionsData: {},
      impactionData: state.impactionData || {}
    };

    // Add dimensions data
    mainData.dimensionsData = {
      pdfFile: {}, // Don't send file object in JSON
      croppedImage1: state.dimensionsData?.croppedImage1 ? 'dimensions_cropped_1' : null,
      croppedImage2: state.dimensionsData?.croppedImage2 ? 'dimensions_cropped_2' : null
    };

    // Process region details directly from regionFormStates (Single Source of Truth)
    if (state.regionFormStates) {
      console.log('🔍 RegionFormStates available for payload:', Object.keys(state.regionFormStates));
      Object.entries(state.regionFormStates).forEach(([regionNumber, regionState]) => {
        const formData = regionState.formData || {};
        const boneQuality = { ...formData.bone_quality };

        if (boneQuality) {
          Object.keys(boneQuality).forEach(key => {
            if (boneQuality[key] === 'Other') {
              boneQuality[key] = boneQuality.otherComments?.[key] || 'Other';
            }
          });
        }

        // Map directly into the shape the API expects
        mainData.regionDetailsData[regionNumber] = {
          formData: {
            pathology_comments: formData.pathology_comments || '',
            predefined_pathology_comments: formData.predefined_pathology_comments || [],
            length_crest: formData.length_crest || '',
            length_crest_to: formData.length_crest_to || '',
            length_periapical: formData.length_periapical || '',
            length_periapical_to: formData.length_periapical_to || '',
            length_lesion: formData.length_lesion || '',
            length_incision: formData.length_incision || '',
            length_quality: formData.length_quality || '',
            width_crest: formData.width_crest || '',
            width_option: formData.width_option || '',
            width_quality: formData.width_quality || '',
            undercut: formData.undercut || '',
            ridge_form: formData.ridge_form || '',
            additional_comments: formData.additional_comments || '',
            width_additional_comments: formData.width_additional_comments || '',
            width_apical_quality: formData.width_apical_quality || false,
            bone_quality: boneQuality || {
              crestal: '',
              buccal: '',
              lingual: '',
              palatal: '',
              labial: '',
              cancellous: ''
            },
            sliceNumber: formData.sliceNumber || '',
            length_varies_from: formData.length_varies_from || '',
            width_2mm: formData.width_2mm || '',
            width_6mm: formData.width_6mm || '',
            width_10mm: formData.width_10mm || ''
          },
          optionalSliceData: regionState.optionalSliceData || {}
        };
      });
    }

    formData.append('data', JSON.stringify(mainData));
    console.log('✅ Added main data to FormData');

    // Add files with enhanced validation and consistent naming
    let fileCount = 0;

    // Convert Base64 back to Blob for submission
    if (state.panoramicViewData?.panoramicFile1?.base64) {
      const fileBlob = base64ToBlob(state.panoramicViewData.panoramicFile1.base64, state.panoramicViewData.panoramicFile1.type);
      formData.append('panoramic_file_1', fileBlob, state.panoramicViewData.panoramicFile1.name);
      console.log(`✅ Added panoramic_file_1: ${state.panoramicViewData.panoramicFile1.name}`);
      fileCount++;
    }

    if (state.panoramicViewData?.panoramicFile2?.base64) {
      const fileBlob = base64ToBlob(state.panoramicViewData.panoramicFile2.base64, state.panoramicViewData.panoramicFile2.type);
      formData.append('panoramic_file_2', fileBlob, state.panoramicViewData.panoramicFile2.name);
      console.log(`✅ Added panoramic_file_2: ${state.panoramicViewData.panoramicFile2.name}`);
      fileCount++;
    }

    if (state.dimensionsData?.croppedImage1?.base64) {
      const fileBlob = base64ToBlob(state.dimensionsData.croppedImage1.base64, state.dimensionsData.croppedImage1.type);
      formData.append('dimensions_cropped_1', fileBlob, state.dimensionsData.croppedImage1.name);
      console.log(`✅ Added dimensions_cropped_1: ${state.dimensionsData.croppedImage1.name}`);
      fileCount++;
    }

    if (state.dimensionsData?.croppedImage2?.base64) {
      const fileBlob = base64ToBlob(state.dimensionsData.croppedImage2.base64, state.dimensionsData.croppedImage2.type);
      formData.append('dimensions_cropped_2', fileBlob, state.dimensionsData.croppedImage2.name);
      console.log(`✅ Added dimensions_cropped_2: ${state.dimensionsData.croppedImage2.name}`);
      fileCount++;
    }

    // Convert Base64 back to Blob for sinus view submission
    if (state.sinusViewData?.image?.base64) {
      const fileBlob = base64ToBlob(state.sinusViewData.image.base64, state.sinusViewData.image.type);
      formData.append('sinus_view_image', fileBlob, state.sinusViewData.image.name);
      console.log(`✅ Added sinus_view_image: ${state.sinusViewData.image.name}`);
      fileCount++;
    }

    if (state.sagittalViewData?.image?.base64) {
      const fileBlob = base64ToBlob(state.sagittalViewData.image.base64, state.sagittalViewData.image.type);
      formData.append('sagittal_view_image', fileBlob, state.sagittalViewData.image.name);
      console.log(`✅ Added sagittal_view_image: ${state.sagittalViewData.image.name}`);
      fileCount++;
    }

    // Add region files with Base64 conversion directly from regionFormStates
    if (state.regionFormStates) {
      console.log('🔍 RegionFormStates available for files:', Object.keys(state.regionFormStates));
      Object.entries(state.regionFormStates).forEach(([regionNumber, regionState]) => {

        console.log(`🔍 Processing region files ${regionNumber}:`, {
          hasImageFile1: !!regionState.croppedImageFile1,
          hasImageFile2: !!regionState.croppedImageFile2,
          imageFile1Structure: regionState.croppedImageFile1 ? Object.keys(regionState.croppedImageFile1) : 'none',
          imageFile2Structure: regionState.croppedImageFile2 ? Object.keys(regionState.croppedImageFile2) : 'none'
        });
        if (regionState.croppedImageFile1?.base64) {
          const fileBlob = base64ToBlob(regionState.croppedImageFile1.base64, regionState.croppedImageFile1.type);
          formData.append(`region_${regionNumber}_image1`, fileBlob, regionState.croppedImageFile1.name);
          console.log(`✅ Added region_${regionNumber}_image1: ${regionState.croppedImageFile1.name}`);
          fileCount++;
        }
        if (regionState.croppedImageFile2?.base64) {
          const fileBlob = base64ToBlob(regionState.croppedImageFile2.base64, regionState.croppedImageFile2.type);
          formData.append(`region_${regionNumber}_image2`, fileBlob, regionState.croppedImageFile2.name);
          console.log(`✅ Added region_${regionNumber}_image2: ${regionState.croppedImageFile2.name}`);
          fileCount++;
        }
        if (regionState.optionalSliceData?.sliceImageFile && regionState.optionalSliceData.sliceImageFile instanceof File) {
          formData.append(`region_${regionNumber}_optional_slice`, regionState.optionalSliceData.sliceImageFile);
          console.log(`✅ Added region_${regionNumber}_optional_slice: ${regionState.optionalSliceData.sliceImageFile.name}`);
          fileCount++;
        }
      });
    }

    console.log(`🔧 Total files attached: ${fileCount}`);
    return formData;
  }, [state]);

  const submitForm = useCallback(async (apiUrl) => {
    try {
      console.log('🚀 Starting form submission...');
      actions.setLoading(true);
      actions.clearError();

      const formData = prepareFormData();

      console.log(`📡 Sending request to: ${process.env.REACT_APP_API_BASE_URL}${apiUrl}`);

      // Use the configured axios instance with proper error handling
      const response = await api.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob', // Important: Expect blob response for file download
        timeout: 300000, // 5 minute timeout for large files
      });

      console.log('✅ Response received:', {
        status: response.status,
        contentType: response.headers['content-type'],
        size: response.data.size
      });

      // Handle blob response for file download
      if (response.headers['content-type']?.includes('application/vnd.openxmlformats') ||
        response.headers['content-type']?.includes('application/msword')) {

        console.log('📄 Processing document download...');

        const blob = response.data;
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);

        // Extract filename from content-disposition header or use default
        const contentDisposition = response.headers['content-disposition'];
        let filename = `CBCT_Report_${new Date().toISOString().split('T')[0]}.docx`;

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Cleanup blob URL
        setTimeout(() => {
          window.URL.revokeObjectURL(link.href);
        }, 100);

        // Check if we should also download the JSON data
        if (process.env.REACT_APP_DOWNLOAD_JSON_WITH_REPORT === 'true') {
          console.log('💾 JSON download enabled, preparing data file...');

          try {
            // Serialize current state in standard format
            const jsonData = serializeFormState(state);
            const jsonString = JSON.stringify(jsonData, null, 2);
            const jsonBlob = new Blob([jsonString], { type: 'application/json' });

            // Generate JSON filename based on report filename
            const jsonFilename = filename.replace('.docx', '.json');

            // Convert blob to URL
            const jsonUrl = window.URL.createObjectURL(jsonBlob);

            // Create temporary link and trigger download with a slight delay
            // to insure the browser handles the first download correctly
            setTimeout(() => {
              const jsonLink = document.createElement('a');
              jsonLink.href = jsonUrl;
              jsonLink.download = jsonFilename;
              document.body.appendChild(jsonLink);
              jsonLink.click();
              document.body.removeChild(jsonLink);

              // Cleanup JSON blob URL
              setTimeout(() => {
                window.URL.revokeObjectURL(jsonLink.href);
              }, 100);

              console.log(`✅ JSON data downloaded successfully: ${jsonFilename}`);
            }, 500); // 500ms delay

          } catch (jsonError) {
            console.error('❌ Failed to prepare JSON download:', jsonError);
            // Don't fail the whole operation if JSON download fails, as report is more important
          }
        }

        console.log(`✅ Report downloaded successfully: ${filename}`);
        return { success: true, type: 'download', filename };
      }

      // Handle JSON response (shouldn't happen for report generation, but just in case)
      try {
        const text = await response.data.text();
        const result = JSON.parse(text);
        console.log('✅ JSON response received:', result);
        return { success: true, data: result };
      } catch (parseError) {
        console.warn('⚠️ Could not parse response as JSON, treating as successful blob download');
        return { success: true, type: 'unknown_blob' };
      }

    } catch (error) {
      console.error('❌ Form submission failed:', error);

      let errorMessage = 'Failed to generate report. Please try again.';

      // Enhanced error handling
      if (error.response) {
        // Server responded with error status
        console.error('Server error response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          headers: error.response.headers
        });

        if (error.response.status === 404) {
          errorMessage = 'Report generation endpoint not found. Please check if the backend server is running.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error during report generation. Please check the backend logs.';
        } else if (error.response.status === 413) {
          errorMessage = 'Files too large. Please reduce image sizes and try again.';
        } else if (error.response.status === 400) {
          // Try to get detailed error from response
          try {
            const errorText = await error.response.data.text();
            if (errorText.includes('<!DOCTYPE')) {
              errorMessage = 'Server returned HTML error page. Check backend server status.';
            } else {
              const errorData = JSON.parse(errorText);
              errorMessage = errorData.detail || errorData.message || 'Invalid request data.';
            }
          } catch (parseError) {
            errorMessage = 'Bad request. Please check your data and try again.';
          }
        }
      } else if (error.request) {
        // Network error
        console.error('Network error:', error.request);
        errorMessage = 'Network error. Please check your connection and backend server status.';
      } else if (error.code === 'ECONNABORTED') {
        // Timeout error
        errorMessage = 'Request timeout. The report generation is taking too long. Please try again.';
      } else {
        // Other error
        console.error('Unexpected error:', error.message);
        errorMessage = `Unexpected error: ${error.message}`;
      }

      actions.setError(errorMessage);
      return { success: false, error: errorMessage };

    } finally {
      actions.setLoading(false);
    }
  }, [prepareFormData, actions]);

  return { submitForm, prepareFormData };
};